import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, AlertCircle, ExternalLink, Eye, Scale, ShieldCheck } from "lucide-react";

import { api } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqList from "@/components/FaqList";
import { BreadcrumbJsonLd, FaqJsonLd, OrganizationJsonLd } from "@/components/Schemas";

export default function ThemeIntent() {
  const location = useLocation();
  const theme = location.pathname.replace(/^\//, "");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://checkexpert.be";

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/seo-pages/theme/${theme}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [theme]);

  if (notFound) return <div className="py-20 text-center text-ink-soft font-modern">Pagina niet gevonden. <Link to="/checks" className="text-[#002FA7] underline">Alle checks</Link></div>;
  if (!data) return <div className="py-16 text-center text-ink-soft font-modern">Laden…</div>;

  const { checker, content, checker_slug, label, doc_part } = data;
  const breadcrumbs = [
    { name: "Alle checks", url: "/checks" },
    { name: label, url: `/${theme}` },
  ];

  return (
    <div className="bg-background font-modern" data-testid="theme-intent">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.meta_description} />
        <link rel="canonical" href={`${origin}/${theme}`} />
        <link rel="alternate" hrefLang="nl-BE" href={`${origin}/${theme}`} />
        <link rel="alternate" hrefLang="nl-NL" href={`${origin}/${theme}`} />
        <link rel="alternate" hrefLang="x-default" href={`${origin}/${theme}`} />
      </Helmet>
      <OrganizationJsonLd origin={origin} />
      <BreadcrumbJsonLd items={breadcrumbs} origin={origin} />
      <FaqJsonLd faqs={content.faqs} />

      <div className="max-w-5xl mx-auto px-6 pt-10 pb-20">
        <Breadcrumbs items={breadcrumbs} />

        {/* HERO — answer-first */}
        <header className="mt-6">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7]">{checker.category_label}</span>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-medium text-ink mt-3 leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
            {data.h1}
          </h1>
          {/* Answer-first paragraph */}
          <p className="text-ink text-lg mt-5 max-w-2xl leading-relaxed font-medium">{content.intent_intro}</p>
          <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">
            Met <Link to={`/checks/${checker_slug}`} className="text-[#002FA7] font-semibold underline-offset-4 hover:underline">{checker.display_name || checker.name}</Link> krijg je in 60 seconden een rapport met score, bevindingen en concrete actiestappen — getoetst aan BE/NL-wetgeving.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/check/${checker_slug}/analyse`)}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold shadow-lg shadow-blue-600/20 transition-all hover:shadow-xl"
              data-testid="intent-cta-start"
            >
              Laat je {doc_part} controleren — €4,99 <ArrowRight className="h-4 w-4" />
            </button>
            <Link to={`/checks/${checker_slug}`} className="text-sm font-semibold text-ink-soft hover:text-ink underline-offset-4 hover:underline" data-testid="intent-link-brand">
              Meer over {checker.display_name || checker.name} →
            </Link>
          </div>
          <p className="text-xs text-ink-subtle mt-5">Laatst bijgewerkt: {data.last_updated} · Onafhankelijk · GDPR-conform</p>
        </header>

        {/* H2: Waar moet je op letten — concrete sample findings + what_checks */}
        <section className="mt-14" data-testid="intent-watchout">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Waar moet je op letten?
          </h2>
          <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">
            Bij een {doc_part} zijn dit de belangrijkste controlepunten. Mensen lopen vaak voor één van deze risico's tegen problemen aan — vaak omdat ze in kleine lettertjes verstopt zitten.
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
            {content.what_checks.map((c, i) => (
              <li key={i} className="flex items-start gap-2.5 rounded-xl bg-white border border-border p-4">
                <Eye className="h-4 w-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-ink leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* H2: Risico's bij niet controleren */}
        <section className="mt-14" data-testid="intent-risks">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Welke risico's loop je als je je {doc_part} niet laat nakijken?
          </h2>
          <div className="mt-5 grid md:grid-cols-2 gap-3">
            {content.common_mistakes.map((m, i) => (
              <div key={i} className="rounded-xl bg-red-50/40 border border-red-100 p-4 flex items-start gap-2.5">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-ink leading-relaxed">{m}</span>
              </div>
            ))}
          </div>
        </section>

        {/* H2: Wettelijk kader BE+NL */}
        <section className="mt-14" data-testid="intent-laws">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Wettelijk kader voor {doc_part}
          </h2>
          <p className="text-ink-soft mt-3 max-w-2xl leading-relaxed">
            We toetsen elk document tegen de geldende BE- en NL-wetgeving. Per bevinding linken we naar het concrete wetsartikel.
          </p>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <LawBlock title="🇧🇪 België" laws={content.laws_be} />
            <LawBlock title="🇳🇱 Nederland" laws={content.laws_nl} />
          </div>
        </section>

        {/* H2: Kosten van je {doc_part} laten nakijken */}
        <section className="mt-14 rounded-2xl bg-white border border-border p-7" data-testid="intent-cost">
          <h2 className="font-editorial text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Wat kost een {doc_part} laten controleren?
          </h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
            <div className="rounded-xl border border-border p-4">
              <div className="text-[11px] uppercase tracking-wider text-ink-soft font-bold">Specialist (advocaat, expert)</div>
              <div className="font-editorial text-2xl text-ink mt-1">€100 – €300</div>
              <p className="text-ink-soft text-xs mt-2 leading-relaxed">Diepgaand advies maar duur, doorgaans 1-2 uur consult vereist.</p>
            </div>
            <div className="rounded-xl border-2 border-[#002FA7] bg-blue-50/40 p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#002FA7] font-bold">CheckExpert</div>
              <div className="font-editorial text-2xl text-ink mt-1">€4,99</div>
              <p className="text-ink-soft text-xs mt-2 leading-relaxed">Onafhankelijke check in 60 sec. Score + concrete actiestappen.</p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-[11px] uppercase tracking-wider text-ink-soft font-bold">Zelf doen (Google)</div>
              <div className="font-editorial text-2xl text-ink mt-1">Gratis</div>
              <p className="text-ink-soft text-xs mt-2 leading-relaxed">Tijdrovend, geen score, geen garantie op volledigheid.</p>
            </div>
          </div>
        </section>

        {/* H2: FAQ */}
        <section className="mt-14" data-testid="intent-faq">
          <h2 className="font-editorial text-3xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Veelgestelde vragen over {doc_part} controleren
          </h2>
          <FaqList faqs={content.faqs} />
        </section>

        {/* Sources */}
        <section className="mt-14" data-testid="intent-sources">
          <h2 className="font-editorial text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
            Officiële bronnen
          </h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2">
            {content.official_sources.map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline underline-offset-4">
                  <ExternalLink className="h-3.5 w-3.5" /> {s.label}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Bottom CTA */}
        <section className="mt-14 rounded-3xl bg-gradient-to-br from-[#002FA7] to-[#001a5c] text-white p-10 sm:p-12 text-center" data-testid="intent-cta-bottom">
          <ShieldCheck className="h-9 w-9 mx-auto text-blue-200" />
          <h2 className="font-editorial text-3xl sm:text-4xl font-medium mt-4" style={{ letterSpacing: "-0.025em" }}>
            Laat je {doc_part} nakijken voor je tekent
          </h2>
          <p className="text-blue-100 mt-3 max-w-xl mx-auto">Onafhankelijk, in 60 sec, voor €4,99. Met concrete actiestappen.</p>
          <button
            type="button"
            onClick={() => navigate(`/check/${checker_slug}/analyse`)}
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-ink font-semibold hover:bg-blue-50 transition-colors"
            data-testid="intent-bottom-cta"
          >
            Start je analyse <ArrowRight className="h-4 w-4" />
          </button>
        </section>

        <p className="text-[11px] text-ink-subtle text-center mt-8 max-w-2xl mx-auto leading-relaxed">
          Uitgever: Webtron BV (BE0793944703), Waregem (BE). CheckExpert vervangt geen juridisch, financieel of medisch advies. Bij twijfel: raadpleeg een specialist.
        </p>
      </div>
    </div>
  );
}

function LawBlock({ title, laws }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5">
      <h3 className="font-editorial text-base font-semibold text-ink mb-3 inline-flex items-center gap-2">
        <Scale className="h-4 w-4 text-[#002FA7]" />
        {title}
      </h3>
      <ul className="space-y-3">
        {laws.map((law, i) => (
          <li key={i} className="text-sm">
            <a href={law.source_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#002FA7] hover:underline underline-offset-4">
              {law.ref} <ExternalLink className="h-3 w-3 inline-block ml-0.5 mb-0.5" />
            </a>
            <p className="text-ink-soft text-[13px] mt-1 leading-relaxed">{law.summary}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
